/**
 * Personal details store – single source of truth for profile personal details.
 *
 * Scalable pattern for other profile sections (gallery, pricing, etc.):
 * 1. Store holds: data, isLoading, saveLoading, error, hasFetchedInSession.
 * 2. ensureFetched(): fetches GET only if !hasFetchedInSession; call on section mount.
 * 3. UI reads from store; sync store.data into local form state when data changes.
 * 4. updateSection(payload): PUT then GET; updates store so UI reflects new data.
 * Result: GET runs once per session when user first opens the section, and again
 * after each successful PUT; no refetch on every visit.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { ArtistService } from "@/services/artistService";
import { inferIndianStateFromPlace } from "@/services/pincodeService";
import { useArtistDetailsStore } from "@/stores/artistDetailsStore";
import type { PersonalDetailsData, PersonalDetailsPayload } from "@/services/artistService";
import type { PostOffice } from "@/services/pincodeService";

export type { PersonalDetailsData, PersonalDetailsPayload };

/** Cache of pincode API results by pincode so we don't refetch on every visit; options also saved on Save. */
export type LocationOptionsCache = Record<string, PostOffice[]>;

interface PersonalDetailsState {
  data: PersonalDetailsData | null;
  isLoading: boolean;
  saveLoading: boolean;
  error: string | null;
  /** True after first successful fetch in this tab session; avoids refetch on every visit */
  hasFetchedInSession: boolean;
  /** Location dropdown options by pincode; used so pincode API only runs when user changes pincode and cache miss */
  cachedLocationOptions: LocationOptionsCache;

  setData: (data: PersonalDetailsData | null) => void;
  /** Fetches GET and updates store. Deduplicated by in-flight promise. */
  fetchPersonalDetails: () => Promise<PersonalDetailsData | null>;
  /** Fetches only if !hasFetchedInSession. Call when user opens the section. */
  ensureFetched: () => Promise<PersonalDetailsData | null>;
  /** PUT then GET; updates store on success. */
  updatePersonalDetails: (payload: PersonalDetailsPayload) => Promise<void>;
  /** Cache location options for a pincode (after pincode API or after save). */
  setCachedLocationOptions: (pincode: string, options: PostOffice[]) => void;
  /** Get cached options for a pincode, if any. */
  getCachedLocationOptions: (pincode: string) => PostOffice[] | undefined;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  /** Call on logout to reset session cache. */
  reset: () => void;
}

let inFlightFetch: Promise<PersonalDetailsData | null> | null = null;

const initialState = {
  data: null,
  isLoading: false,
  saveLoading: false,
  error: null,
  hasFetchedInSession: false,
  cachedLocationOptions: {} as LocationOptionsCache,
};

export const usePersonalDetailsStore = create<PersonalDetailsState>()(
  persist(
    (set, get) => ({
  ...initialState,

  setData: (data) => set({ data, error: null }),

  fetchPersonalDetails: async () => {
    if (inFlightFetch) return inFlightFetch;
    inFlightFetch = (async () => {
      set({ isLoading: true, error: null });
      try {
        const data = await ArtistService.getPersonalDetails();
        set({
          data,
          error: null,
          isLoading: false,
          hasFetchedInSession: true,
        });
        void useArtistDetailsStore.getState().fetchArtistDetails();
        return data;
      } catch (err) {
        set({ error: "Failed to load personal details", isLoading: false });
        return null;
      } finally {
        inFlightFetch = null;
      }
    })();
    return inFlightFetch;
  },

  ensureFetched: async () => {
    const { hasFetchedInSession, fetchPersonalDetails, data } = get();
    if (hasFetchedInSession) return data;
    return fetchPersonalDetails();
  },

  updatePersonalDetails: async (payload) => {
    set({ saveLoading: true, error: null });
    try {
      const normalizedPayload: PersonalDetailsPayload = {
        ...payload,
        location: payload.location.map((item) => ({
          ...item,
          state:
            item.state.trim() ||
            inferIndianStateFromPlace(item.city, item.city),
        })),
      };
      await ArtistService.updatePersonalDetails(normalizedPayload);
      await get().fetchPersonalDetails();
    } catch (err) {
      set({
        error: "Failed to save. Please try again.",
        saveLoading: false,
      });
      throw err;
    } finally {
      set({ saveLoading: false });
    }
  },

  setCachedLocationOptions: (pincode, options) => {
    if (!pincode || options.length === 0) return;
    set((s) => ({
      cachedLocationOptions: { ...s.cachedLocationOptions, [pincode]: options },
    }));
  },

  getCachedLocationOptions: (pincode) => {
    return get().cachedLocationOptions[pincode];
  },

  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),

  reset: () => {
    inFlightFetch = null;
    set(initialState);
  },
}),
    {
      name: "personal-details-storage",
      partialize: (s) => ({ cachedLocationOptions: s.cachedLocationOptions }),
    }
  )
);
